import React, { createContext, useContext, ReactNode } from 'react'
import { useResponsiveNavigation, useNavigationState, useNavigationAnalytics } from '../hooks/useResponsiveNavigation'

interface NavigationContextType {
  // Responsive navigation
  navigationState: ReturnType<typeof useResponsiveNavigation>['navigationState']
  screenSize: ReturnType<typeof useResponsiveNavigation>['screenSize']
  deviceType: ReturnType<typeof useResponsiveNavigation>['deviceType']
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  shouldShowMobileMenu: boolean
  shouldShowSidebar: boolean
  shouldShowBreadcrumbs: boolean
  
  // Actions
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  openMobileMenu: () => void
  toggleSidebar: () => void
  collapseSidebar: () => void
  expandSidebar: () => void
  setActiveSection: (section: string | null) => void
  
  // Navigation state
  navigationHistory: string[]
  navigationDepth: number
  addToHistory: (path: string) => void
  goBack: () => void
  canGoBack: boolean
  
  // Analytics
  navigationMetrics: ReturnType<typeof useNavigationAnalytics>['navigationMetrics']
  trackNavigation: (section: string, duration?: number) => void
  trackNavigationError: () => void
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

interface NavigationProviderProps {
  children: ReactNode
  config?: Parameters<typeof useResponsiveNavigation>[0]
}

export function NavigationProvider({ children, config }: NavigationProviderProps) {
  const responsiveNav = useResponsiveNavigation(config)
  const navState = useNavigationState()
  const navAnalytics = useNavigationAnalytics()

  const contextValue: NavigationContextType = {
    // Responsive navigation
    navigationState: responsiveNav.navigationState,
    screenSize: responsiveNav.screenSize,
    deviceType: responsiveNav.deviceType,
    isMobile: responsiveNav.isMobile,
    isTablet: responsiveNav.isTablet,
    isDesktop: responsiveNav.isDesktop,
    shouldShowMobileMenu: responsiveNav.shouldShowMobileMenu,
    shouldShowSidebar: responsiveNav.shouldShowSidebar,
    shouldShowBreadcrumbs: responsiveNav.shouldShowBreadcrumbs,
    
    // Actions
    toggleMobileMenu: responsiveNav.toggleMobileMenu,
    closeMobileMenu: responsiveNav.closeMobileMenu,
    openMobileMenu: responsiveNav.openMobileMenu,
    toggleSidebar: responsiveNav.toggleSidebar,
    collapseSidebar: responsiveNav.collapseSidebar,
    expandSidebar: responsiveNav.expandSidebar,
    setActiveSection: responsiveNav.setActiveSection,
    
    // Navigation state
    navigationHistory: navState.navigationHistory,
    navigationDepth: navState.navigationDepth,
    addToHistory: navState.addToHistory,
    goBack: navState.goBack,
    canGoBack: navState.canGoBack,
    
    // Analytics
    navigationMetrics: navAnalytics.navigationMetrics,
    trackNavigation: navAnalytics.trackNavigation,
    trackNavigationError: navAnalytics.trackNavigationError
  }

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation() {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}

// Hook for navigation shortcuts
export function useNavigationShortcuts() {
  const {
    toggleMobileMenu,
    toggleSidebar,
    shouldShowMobileMenu,
    goBack,
    canGoBack
  } = useNavigation()

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Global shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'b':
            event.preventDefault()
            if (shouldShowMobileMenu) {
              toggleMobileMenu()
            } else {
              toggleSidebar()
            }
            break
          case 'm':
            event.preventDefault()
            if (shouldShowMobileMenu) {
              toggleMobileMenu()
            }
            break
        }
      }

      // Navigation shortcuts
      if (event.altKey) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault()
            if (canGoBack) {
              goBack()
            }
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleMobileMenu, toggleSidebar, shouldShowMobileMenu, goBack, canGoBack])
}

// Hook for responsive navigation behavior
export function useResponsiveNavigationBehavior() {
  const {
    isMobile,
    isTablet,
    isDesktop,
    shouldShowMobileMenu,
    shouldShowSidebar,
    shouldShowBreadcrumbs,
    navigationState,
    closeMobileMenu,
    collapseSidebar
  } = useNavigation()

  // Auto-close mobile menu on desktop
  React.useEffect(() => {
    if (isDesktop && navigationState.isMobileMenuOpen) {
      closeMobileMenu()
    }
  }, [isDesktop, navigationState.isMobileMenuOpen, closeMobileMenu])

  // Auto-collapse sidebar on mobile
  React.useEffect(() => {
    if (isMobile && !navigationState.isSidebarCollapsed) {
      collapseSidebar()
    }
  }, [isMobile, navigationState.isSidebarCollapsed, collapseSidebar])

  return {
    isMobile,
    isTablet,
    isDesktop,
    shouldShowMobileMenu,
    shouldShowSidebar,
    shouldShowBreadcrumbs,
    navigationState
  }
}
